import { ChangeDetectorRef, Component, Input, OnInit, computed, Signal, signal } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngxs/store';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { SavedView } from 'app/core/models';
import { RelatedToType } from 'app/shared/enums';
import { SavedViewsActions } from 'app/state/saved-view/action';
import { SavedViewsState } from 'app/state/saved-view/state';
import { Subject, map, switchMap, tap } from 'rxjs';
import { TSTreeNode, TSTreeMenuModule } from 'tsui';
import { plural } from 'pluralize';
import { PermissionUtils } from '@core/utils/permission.utils';
import { toSignal } from '@angular/core/rxjs-interop';
import { SavedViewsSidebarService } from '@shared/services/saved-views-sidebar.service';

@Component({
  selector: 'app-saved-view-sidebar',
  template: `<div class="app-saved-view-sidebar">
    <ng-content></ng-content>
    <ts-tree-menu [suppressHeader]="true" [items]="menuItems()" [loading]="loading()"></ts-tree-menu>
  </div>`,
  imports: [TSTreeMenuModule],
  standalone: true,
})
export class SavedViewSidebarComponent extends OnDestroyMixin implements OnInit {
  @Input() set relatedToType(value: RelatedToType) {
    this._relatedToType = value;
    this.loadViewsTrigger.next();
  }

  @Input() ignoreUrlPaths: string[] = [];

  private _relatedToType: RelatedToType = RelatedToType.Company;

  private readonly savedViews: Signal<SavedView[]> = toSignal(
    this.store.select(SavedViewsState.getViews).pipe(
      map((savedViews) => {
        return savedViews
          ? savedViews
              .filter((savedView) => savedView.related_to_type === this.relatedToType)
              .filter(PermissionUtils.canView)
          : [];
      }),
    ),
  );

  get relatedToType(): RelatedToType {
    return this._relatedToType;
  }

  loading = signal(true);
  menuItems: Signal<TSTreeNode[]> = computed(() => {
    if (this.savedViews()) {
      return this.savedViews()
        .sort(SavedViewsSidebarService.sortSavedViews)
        .map((savedView) => this.getSidebarMenuItem(savedView));
    } else {
      return [];
    }
  });
  readonly loadViewsTrigger = new Subject<void>();

  constructor(
    private store: Store,
    private matDialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit() {
    this.loadViewsTrigger
      .pipe(
        tap(() => this.loading.set(true)),
        switchMap(() => {
          return this.store.dispatch(new SavedViewsActions.LoadViews());
        }),
        untilComponentDestroyed(this),
      )
      .subscribe(() => {
        this.loading.set(false);
      });

    this.loadViewsTrigger.next();
  }

  getSidebarMenuItem(savedView: SavedView): TSTreeNode {
    let url = '';
    const urlMap = {
      AggregatedTasks: `/tasks/${plural(this.relatedToType.toLocaleLowerCase())}/${savedView.id}/table`,
    };

    url = this.customObjectSavedViewUrl(savedView);

    const hasQueryParams = Object.keys(this.activatedRoute.snapshot.queryParams).length > 0;
    if (hasQueryParams) {
      url = `${url}?${new URLSearchParams(this.activatedRoute.snapshot.queryParams).toString()}`;
    }

    return {
      id: savedView.id.toString(),
      name: savedView.name,
      icon: 'view_list',
      link: url,
    };
  }

  private customObjectSavedViewUrl(savedView) {
    // Handle saved view sidebar routes for custom objects inside projects
    if (
      this.router.url.includes('custom_object') &&
      (this.router.url.includes('projects') || this.router.url.includes('properties'))
    ) {
      const sliceQueryParamRange = this.router.url.indexOf('?') > -1 ? [0, this.router.url.indexOf('?')] : [];
      const firstPart = this.router.url.slice(...sliceQueryParamRange).split('/');
      const secondPart = `/${savedView.id}/table`;
      const sliceRange = firstPart.includes('table') ? [0, -2] : [];

      return `${firstPart.slice(...sliceRange).join('/')}${secondPart}`;
    }

    let currentUrl =
      this.ignoreUrlPaths.length > 0 ? this.filterIgnoredPathsFromCurrentUrl(this.router.url) : this.router.url;
    const urlParts = currentUrl.split('/');
    const hasLink = urlParts.includes('link');
    let linkId = hasLink ? urlParts[urlParts.length - 1].split('?')[0] : '';

    if (linkId === 'table') {
      linkId = '';
    }

    if (urlParts.length > 3) {
      currentUrl = urlParts.slice(0, hasLink && linkId !== '' ? -1 : -2).join('/');
    }

    return `${currentUrl}${linkId ? `/${linkId}` : ''}/${savedView.id}/table`;
  }

  private filterIgnoredPathsFromCurrentUrl(currentUrl: string): string {
    return `/${currentUrl
      .split('/')
      .filter((path) => path !== '')
      .filter((path) => !this.ignoreUrlPaths.includes(path))
      .join('/')}`;
  }
}
